import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Globe, Lock } from 'lucide-react'
import { useWizardStepConfig } from './SetupWizardLayout'
import {
  markStepCompleted,
  markStepSkipped,
  readDraft,
  writeDraft,
} from './signupState'
import { dialCodeFor } from './countries'
import { triggerSetupCompletingTransition } from './setupCompletingTransitionController'
import { persistSignupCommunityFromDraft } from '../../lib/signupCommunityPersist'
import { writeLastPortal } from '../../utils/lastPortal'
import {
  communityLoginRedirectState,
} from '../../utils/existingCommunityCheck'
import {
  MANAGEMENT_PLATFORMS,
  getPlatformPlaceholder,
} from '../../lib/managementPlatforms'
import { migrateCommunityIsPrivate, COMMUNITY_PRIVACY_OPTIONS } from '../../lib/communityPrivacy'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../shadcn/ui/select'

/**
 * SetupWizardPolicies — Wizard Step 3 ("Set up how your community runs.").
 *
 * Replaces the previous Branding step (logo upload + accent picker).
 * Logo capture moved to /signup/community; accent customization was
 * dropped entirely. This step now adapts to how the organizer already
 * runs their community:
 *
 *   1. Diagnostic — pick "WhatsApp groups" or "A dedicated platform".
 *      No default. Required to unlock Section 3.
 *   2. Visibility — public / private radio cards (default 'public').
 *   3. Conditional contact / integration fields — fades in once the
 *      diagnostic is answered. WhatsApp branch captures number +
 *      group link; app branch captures platform + handle + integrations
 *      interest. Switching diagnostic preserves the inactive branch's
 *      draft fields so users can flip back without losing data.
 *
 * Persistence:
 *   - Radio / select / checkbox writes hit sessionStorage immediately.
 *   - Text inputs debounce 500ms before writing, batched per typing burst.
 *   - flushPending() on Continue / Skip clears timers and writes the
 *     latest values synchronously to dodge a race with the 500ms timer.
 *
 * On Continue: persists current values, marks the step completed,
 * fires the SetupCompletingTransition climax overlay, then navigates
 * to /community/welcome. The overlay is mounted at app root so it
 * survives this route unmounting.
 *
 * On Skip: persists current values (so anything the user typed sticks
 * even after skipping), marks the step skipped, and navigates straight
 * to /community/welcome without firing the climax overlay — the user
 * opted out of the wizard's final beat, so we don't celebrate it.
 */

const STEP = 'policies'
const NEXT_ROUTE = '/community/dashboard'
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]

const MANAGEMENT_OPTIONS = [
  {
    id: 'whatsapp',
    title: 'WhatsApp groups',
    description: 'Most coordination happens in group chats.',
  },
  {
    id: 'app',
    title: 'A dedicated platform',
    description: 'You use an app like Spond, TeamSnap, or your own tool.',
  },
]

const JOIN_POLICY_ICONS = {
  public: Globe,
  private: Lock,
}

// Per-country phone placeholders. Lives here (not in countries.js)
// because it's purely a UI affordance — we don't validate the format
// and don't need it anywhere else.
const PHONE_PLACEHOLDER_BY_ISO = {
  LB: '71 123 456',
  AE: '50 123 4567',
  GB: '7700 900123',
  SA: '50 123 4567',
  QA: '3312 3456',
  KW: '500 12345',
  JO: '79 123 4567',
  EG: '100 123 4567',
  US: '555 123 4567',
  CA: '416 555 0199',
  FR: '6 12 34 56 78',
  DE: '1512 3456789',
  ES: '612 34 56 78',
  IT: '312 345 6789',
  TR: '532 123 4567',
  IN: '99876 54321',
}
const DEFAULT_PHONE_PLACEHOLDER = 'Phone number'

function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return ''
  const A = 0x1f1e6
  const ASCII_A = 0x41
  return String.fromCodePoint(
    iso2.toUpperCase().charCodeAt(0) - ASCII_A + A,
    iso2.toUpperCase().charCodeAt(1) - ASCII_A + A,
  )
}

// Strip everything except digits, spaces, and dashes — matches the
// spec's allowed input set. We don't reformat; the user controls the
// shape so e.g. "+961 71 123 456" round-trips cleanly.
function sanitizePhoneInput(raw) {
  return String(raw || '').replace(/[^\d\s-]/g, '')
}

function looksLikeWhatsappGroupLink(raw) {
  const v = String(raw || '').trim()
  if (!v) return true // empty is fine; only non-empty values get validated
  return v.toLowerCase().startsWith('https://chat.whatsapp.com/')
}

function patchPolicies(patch) {
  const draft = readDraft()
  const wizard = draft.setupWizard || {}
  const prevPolicies = wizard.policies || {}
  writeDraft({
    setupWizard: {
      ...wizard,
      policies: { ...prevPolicies, ...patch },
    },
  })
}

export default function SetupWizardPolicies() {
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  const initialDraft = useMemo(readDraft, [])
  const initialPolicies = initialDraft.setupWizard?.policies || {}
  const draftCountry = (initialDraft.country || '').trim()

  // Diagnostic — no default. User must pick to unlock Section 3.
  const [managementType, setManagementType] = useState(
    initialPolicies.managementType === 'whatsapp' ||
      initialPolicies.managementType === 'app'
      ? initialPolicies.managementType
      : null,
  )

  const [isPrivate, setIsPrivate] = useState(() =>
    migrateCommunityIsPrivate(initialPolicies),
  )

  // WhatsApp branch
  const [whatsappCountryCode] = useState(
    initialPolicies.whatsappNumberCountryCode || draftCountry || '',
  )
  const [phoneInput, setPhoneInput] = useState(
    initialPolicies.whatsappNumber || '',
  )
  const [linkInput, setLinkInput] = useState(
    initialPolicies.whatsappGroupLink || '',
  )
  const [linkBlurred, setLinkBlurred] = useState(false)

  // App branch
  const [currentPlatform, setCurrentPlatform] = useState(
    initialPolicies.currentPlatform || '',
  )
  const [currentPlatformOther, setCurrentPlatformOther] = useState(
    initialPolicies.currentPlatformOther || '',
  )
  const [platformHandle, setPlatformHandle] = useState(
    initialPolicies.platformHandle || '',
  )
  const [integrationsInterest, setIntegrationsInterest] = useState(
    initialPolicies.integrationsInterest !== false, // default true
  )

  const dialCode = dialCodeFor(whatsappCountryCode) || ''
  const phonePlaceholder =
    PHONE_PLACEHOLDER_BY_ISO[whatsappCountryCode] || DEFAULT_PHONE_PLACEHOLDER
  const linkLooksValid = looksLikeWhatsappGroupLink(linkInput)
  const showLinkWarning = linkBlurred && !linkLooksValid
  const platformPlaceholder = getPlatformPlaceholder(currentPlatform)
  const showOtherInput = currentPlatform === 'other'

  const [finalizing, setFinalizing] = useState(false)
  const [finalizeError, setFinalizeError] = useState('')

  /* ─── Immediate writes (radio / select / checkbox) ─── */

  const onPickManagementType = (next) => {
    // Switching does NOT clear the inactive branch's fields — only the
    // managementType itself is updated. The other branch's inputs stay
    // in component state AND in the draft so the user can flip back.
    setManagementType(next)
    patchPolicies({ managementType: next })
  }

  const onPickPrivacy = (nextIsPrivate) => {
    setIsPrivate(nextIsPrivate)
    patchPolicies({ isPrivate: nextIsPrivate })
  }

  const onPickPlatform = (next) => {
    setCurrentPlatform(next)
    patchPolicies({ currentPlatform: next || null })
  }

  const onToggleIntegrationsInterest = (next) => {
    setIntegrationsInterest(next)
    patchPolicies({ integrationsInterest: next })
  }

  /* ─── Debounced text writes (500ms) ─── */

  const phoneTimerRef = useRef(null)
  const linkTimerRef = useRef(null)
  const otherTimerRef = useRef(null)
  const handleTimerRef = useRef(null)

  useEffect(() => {
    if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)
    phoneTimerRef.current = setTimeout(() => {
      const trimmed = phoneInput.trim()
      patchPolicies({
        whatsappNumber: trimmed ? trimmed : null,
        whatsappNumberCountryCode: whatsappCountryCode,
      })
    }, 500)
    return () => {
      if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)
    }
  }, [phoneInput, whatsappCountryCode])

  useEffect(() => {
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current)
    linkTimerRef.current = setTimeout(() => {
      const trimmed = linkInput.trim()
      patchPolicies({ whatsappGroupLink: trimmed ? trimmed : null })
    }, 500)
    return () => {
      if (linkTimerRef.current) clearTimeout(linkTimerRef.current)
    }
  }, [linkInput])

  useEffect(() => {
    if (otherTimerRef.current) clearTimeout(otherTimerRef.current)
    otherTimerRef.current = setTimeout(() => {
      const trimmed = currentPlatformOther.trim()
      patchPolicies({ currentPlatformOther: trimmed ? trimmed : null })
    }, 500)
    return () => {
      if (otherTimerRef.current) clearTimeout(otherTimerRef.current)
    }
  }, [currentPlatformOther])

  useEffect(() => {
    if (handleTimerRef.current) clearTimeout(handleTimerRef.current)
    handleTimerRef.current = setTimeout(() => {
      const trimmed = platformHandle.trim()
      patchPolicies({ platformHandle: trimmed ? trimmed : null })
    }, 500)
    return () => {
      if (handleTimerRef.current) clearTimeout(handleTimerRef.current)
    }
  }, [platformHandle])

  // Flush any pending debounced writes synchronously before navigating
  // away. Otherwise a fast Continue immediately after a keystroke could
  // race the 500ms timer and lose the latest value.
  const flushPending = () => {
    if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current)
    if (otherTimerRef.current) clearTimeout(otherTimerRef.current)
    if (handleTimerRef.current) clearTimeout(handleTimerRef.current)
    const trimmedPhone = phoneInput.trim()
    const trimmedLink = linkInput.trim()
    const trimmedOther = currentPlatformOther.trim()
    const trimmedHandle = platformHandle.trim()
    patchPolicies({
      isPrivate,
      managementType,
      whatsappNumber: trimmedPhone ? trimmedPhone : null,
      whatsappNumberCountryCode: whatsappCountryCode,
      whatsappGroupLink: trimmedLink ? trimmedLink : null,
      currentPlatform: currentPlatform || null,
      currentPlatformOther: trimmedOther ? trimmedOther : null,
      platformHandle: trimmedHandle ? trimmedHandle : null,
      integrationsInterest,
    })
  }

  const finalizeWizard = async ({ celebrate }) => {
    setFinalizing(true)
    setFinalizeError('')

    try {
      const result = await persistSignupCommunityFromDraft()

      if (!result.ok) {
        if (result.redirectTo) {
          navigate(result.redirectTo, {
            replace: true,
            state: result.alreadyInCommunity
              ? communityLoginRedirectState(result.alreadyInCommunity)
              : undefined,
          })
          return
        }
        setFinalizeError(
          result.error || 'Could not create your community. Please try again.',
        )
        return
      }

      writeDraft({
        communityId: result.communityId,
        communityIdJoin: result.communityIdJoin,
        accountStatus: 'activated',
      })
      writeLastPortal('community')

      if (celebrate) {
        triggerSetupCompletingTransition()
      }
      navigate(NEXT_ROUTE, { replace: true })
    } catch (err) {
      console.error('Finalize signup community failed', err)
      setFinalizeError(
        err.message || 'Could not create your community. Please try again.',
      )
    } finally {
      setFinalizing(false)
    }
  }

  const handleContinue = () => {
    flushPending()
    markStepCompleted(STEP)
    finalizeWizard({ celebrate: true })
  }

  const handleSkip = () => {
    // Reset to the documented "skip defaults": public community,
    // managementType null, all branch fields cleared.
    if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current)
    if (otherTimerRef.current) clearTimeout(otherTimerRef.current)
    if (handleTimerRef.current) clearTimeout(handleTimerRef.current)
    patchPolicies({
      isPrivate: false,
      managementType: null,
      whatsappNumber: null,
      whatsappGroupLink: null,
      currentPlatform: null,
      currentPlatformOther: null,
      platformHandle: null,
    })
    markStepSkipped(STEP)
    finalizeWizard({ celebrate: false })
  }

  useWizardStepConfig({
    canContinue: !finalizing,
    onContinue: handleContinue,
    onSkipStep: handleSkip,
  })

  /* ─── Section 3 fade-in / cross-fade between branches ─── */

  const sectionMotion = reduceMotion
    ? { initial: false, animate: { opacity: 1, y: 0 }, exit: { opacity: 1, y: 0 }, transition: { duration: 0 } }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 8 },
        transition: { duration: 0.25, ease: EASE_OUT_EXPO },
      }

  return (
    <>
      <h1
        className="font-medium text-[var(--signup-text-primary)] text-[30px] sm:text-[36px] leading-[1.1]"
        style={{ letterSpacing: '-0.01em' }}
      >
        Set up how your community runs.
      </h1>
      <p className="mt-4 text-[15px] text-[var(--signup-text-secondary)] leading-[1.55]">
        A few choices to tailor your portal.
      </p>

      {finalizeError ? (
        <p role="alert" className="mt-4 text-[14px] text-red-600 leading-snug">
          {finalizeError}
        </p>
      ) : null}

      {/* ─── Section 1 — Privacy ─── */}
      <section className="mt-9" aria-labelledby="wizard-privacy-heading">
        <h2
          id="wizard-privacy-heading"
          className="text-[15px] font-medium text-[var(--signup-text-primary)]"
        >
          Privacy
        </h2>
        <p className="mt-1.5 text-[13px] text-[var(--signup-text-secondary)] leading-[1.55]">
          Control whether your community appears in the Statbox mobile app.
        </p>
        <PrivacyPicker
          ariaLabelledBy="wizard-privacy-heading"
          isPrivate={isPrivate}
          onSelect={onPickPrivacy}
        />
        <p className="mt-3 text-[12px] text-[var(--signup-text-tertiary)] leading-snug">
          Change anytime in Settings.
        </p>
      </section>

      {/* ─── Hairline divider ─── */}
      <div role="presentation" className="mt-8 h-px bg-[var(--signup-border-subtle)]" />

      {/* ─── Section 2 — Diagnostic ─── */}
      <section className="mt-8" aria-labelledby="wizard-management-heading">
        <h2
          id="wizard-management-heading"
          className="text-[15px] font-medium text-[var(--signup-text-primary)]"
        >
          How do you run your community today?
        </h2>
        <RadioCardGroup
          ariaLabelledBy="wizard-management-heading"
          options={MANAGEMENT_OPTIONS}
          selectedId={managementType}
          onSelect={onPickManagementType}
        />
      </section>

      {/* ─── Section 3 — Conditional branch (cross-fades on switch) ─── */}
      <AnimatePresence mode="wait" initial={false}>
        {managementType ? (
          <motion.section
            key={managementType}
            {...sectionMotion}
            className="mt-8"
            aria-labelledby="wizard-branch-heading"
          >
            <div role="presentation" className="h-px bg-[var(--signup-border-subtle)]" />
            <div className="mt-8">
              {managementType === 'whatsapp' ? (
                <WhatsappBranch
                  whatsappCountryCode={whatsappCountryCode}
                  dialCode={dialCode}
                  phonePlaceholder={phonePlaceholder}
                  phoneInput={phoneInput}
                  setPhoneInput={setPhoneInput}
                  linkInput={linkInput}
                  setLinkInput={setLinkInput}
                  setLinkBlurred={setLinkBlurred}
                  showLinkWarning={showLinkWarning}
                />
              ) : (
                <AppBranch
                  currentPlatform={currentPlatform}
                  onPickPlatform={onPickPlatform}
                  showOtherInput={showOtherInput}
                  currentPlatformOther={currentPlatformOther}
                  setCurrentPlatformOther={setCurrentPlatformOther}
                  platformPlaceholder={platformPlaceholder}
                  platformHandle={platformHandle}
                  setPlatformHandle={setPlatformHandle}
                  integrationsInterest={integrationsInterest}
                  onToggleIntegrationsInterest={onToggleIntegrationsInterest}
                />
              )}
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>
    </>
  )
}

/* ───────────── Branch components ───────────── */

function WhatsappBranch({
  whatsappCountryCode,
  dialCode,
  phonePlaceholder,
  phoneInput,
  setPhoneInput,
  linkInput,
  setLinkInput,
  setLinkBlurred,
  showLinkWarning,
}) {
  return (
    <div aria-labelledby="wizard-branch-heading">
      <h2
        id="wizard-branch-heading"
        className="text-[15px] font-medium text-[var(--signup-text-primary)]"
      >
        WhatsApp contact
      </h2>
      <p className="mt-1.5 text-[13px] text-[var(--signup-text-secondary)] leading-[1.55]">
        How players reach you and join your group.
      </p>

      <div className="mt-5 space-y-5">
        {/* WhatsApp number */}
        <div>
          <FieldLabel htmlFor="wizard-policies-whatsapp-number">
            WhatsApp number
          </FieldLabel>
          <div className="flex h-[48px] w-full items-stretch overflow-hidden rounded-lg border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] focus-within:border-[var(--signup-accent)] focus-within:ring-[3px] focus-within:ring-[var(--signup-accent-ring)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150">
            <div className="flex items-center gap-1.5 px-3 text-[15px] text-[var(--signup-text-primary)]">
              <span aria-hidden className="text-[18px] leading-none">
                {flagEmoji(whatsappCountryCode) || '🌐'}
              </span>
              <span className="tabular-nums text-[14px] text-[var(--signup-text-secondary)]">
                {dialCode || '+'}
              </span>
            </div>
            <div aria-hidden className="my-2 w-px bg-[var(--signup-border-subtle)]" />
            <input
              id="wizard-policies-whatsapp-number"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={phoneInput}
              onChange={(e) => setPhoneInput(sanitizePhoneInput(e.target.value))}
              placeholder={phonePlaceholder}
              aria-label="WhatsApp number"
              className="min-w-0 flex-1 bg-transparent px-3 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] focus:outline-none"
            />
          </div>
          <FieldHelp>
            Country code from your signup. Players see this to message you.
          </FieldHelp>
        </div>

        {/* WhatsApp group link */}
        <div>
          <FieldLabel htmlFor="wizard-policies-whatsapp-link">
            WhatsApp group link
          </FieldLabel>
          <input
            id="wizard-policies-whatsapp-link"
            type="url"
            inputMode="url"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onBlur={() => setLinkBlurred(true)}
            placeholder="https://chat.whatsapp.com/..."
            aria-label="WhatsApp group link"
            className="h-[48px] w-full rounded-lg border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] px-4 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] focus:border-[var(--signup-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--signup-accent-ring)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150"
          />
          {showLinkWarning ? (
            <FieldHelp>
              This doesn't look like a WhatsApp group link. Continue anyway?
            </FieldHelp>
          ) : (
            <FieldHelp>Invite link to your community's group chat.</FieldHelp>
          )}
        </div>
      </div>
    </div>
  )
}

function AppBranch({
  currentPlatform,
  onPickPlatform,
  showOtherInput,
  currentPlatformOther,
  setCurrentPlatformOther,
  platformPlaceholder,
  platformHandle,
  setPlatformHandle,
  integrationsInterest,
  onToggleIntegrationsInterest,
}) {
  return (
    <div aria-labelledby="wizard-branch-heading">
      <h2
        id="wizard-branch-heading"
        className="text-[15px] font-medium text-[var(--signup-text-primary)]"
      >
        Your platform
      </h2>
      <p className="mt-1.5 text-[13px] text-[var(--signup-text-secondary)] leading-[1.55]">
        We'll prioritize integrations based on what our users already use.
      </p>

      <div className="mt-5 space-y-5">
        {/* Platform select */}
        <div>
          <FieldLabel htmlFor="wizard-policies-platform">
            Which platform?
          </FieldLabel>
          <Select
            value={currentPlatform || undefined}
            onValueChange={(v) => onPickPlatform(v)}
          >
            <SelectTrigger
              id="wizard-policies-platform"
              aria-label="Which platform?"
              className="h-[48px] w-full rounded-lg border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] px-4 text-[15px] text-[var(--signup-text-primary)] data-[placeholder]:text-[var(--signup-text-tertiary)] shadow-none focus:outline-none focus:ring-[3px] focus:ring-[var(--signup-accent-ring)] focus:border-[var(--signup-accent)] data-[state=open]:ring-[3px] data-[state=open]:ring-[var(--signup-accent-ring)] data-[state=open]:border-[var(--signup-accent)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150"
            >
              <SelectValue placeholder="Select your platform...">
                {currentPlatform
                  ? MANAGEMENT_PLATFORMS.find((p) => p.key === currentPlatform)?.name
                  : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent
              className="font-jakarta max-h-[320px] border-[var(--signup-border-default)] shadow-lg"
              position="popper"
              sideOffset={6}
            >
              {MANAGEMENT_PLATFORMS.map((p) => (
                <SelectItem
                  key={p.key}
                  value={p.key}
                  className="py-2 px-2.5 rounded-md text-[14.5px] text-[var(--signup-text-primary)] focus:bg-[var(--signup-accent-subtle)] focus:text-[var(--signup-text-primary)] data-[state=checked]:font-medium"
                >
                  <span className="flex flex-col leading-tight">
                    <span>{p.name}</span>
                    <span className="text-[12px] text-[var(--signup-text-tertiary)] mt-0.5">
                      {p.description}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Freeform "What do you use?" — only when "Other" is selected */}
        {showOtherInput ? (
          <div>
            <FieldLabel htmlFor="wizard-policies-platform-other">
              What do you use?
            </FieldLabel>
            <input
              id="wizard-policies-platform-other"
              type="text"
              value={currentPlatformOther}
              onChange={(e) => setCurrentPlatformOther(e.target.value)}
              placeholder="Tell us"
              aria-label="What do you use?"
              className="h-[48px] w-full rounded-lg border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] px-4 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] focus:border-[var(--signup-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--signup-accent-ring)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150"
            />
          </div>
        ) : null}

        {/* Platform handle / link */}
        <div>
          <FieldLabel htmlFor="wizard-policies-platform-handle">
            Your account or community link
          </FieldLabel>
          <input
            id="wizard-policies-platform-handle"
            type="url"
            inputMode="url"
            value={platformHandle}
            onChange={(e) => setPlatformHandle(e.target.value)}
            placeholder={platformPlaceholder}
            aria-label="Your account or community link"
            className="h-[48px] w-full rounded-lg border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] px-4 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] focus:border-[var(--signup-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--signup-accent-ring)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150"
          />
          <FieldHelp>Helps us connect with your existing setup later.</FieldHelp>
        </div>

        {/* Integrations interest */}
        <div>
          <label
            htmlFor="wizard-policies-integrations-interest"
            className="flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              id="wizard-policies-integrations-interest"
              type="checkbox"
              checked={integrationsInterest}
              onChange={(e) => onToggleIntegrationsInterest(e.target.checked)}
              className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded border-[var(--signup-border-strong)] text-[var(--signup-accent)] focus:ring-[var(--signup-accent)] focus:ring-offset-2 focus:ring-offset-[var(--signup-bg-base)] cursor-pointer"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-medium text-[var(--signup-text-primary)] leading-snug">
                Notify me when we integrate with my platform
              </span>
              <span className="mt-0.5 block text-[12px] text-[var(--signup-text-tertiary)] leading-snug">
                We'll email you when your platform is supported. No spam.
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  )
}

/* ───────────── Privacy picker (portal-aligned) ───────────── */

function PrivacyPicker({ ariaLabelledBy, isPrivate, onSelect }) {
  return (
    <div
      className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-4"
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
    >
      {COMMUNITY_PRIVACY_OPTIONS.map((opt) => {
        const selected = opt.id === 'private' ? isPrivate : !isPrivate
        const Icon = JOIN_POLICY_ICONS[opt.id] || Globe
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(opt.id === 'private')}
            className={[
              'flex-1 text-left rounded-xl p-4',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)]',
              'motion-safe:transition-[border-color,background-color,box-shadow] motion-safe:duration-150',
              selected
                ? 'border-2 border-[var(--signup-accent)] bg-[rgba(14,154,160,0.04)]'
                : 'border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-elevated)] hover:border-[var(--signup-border-strong)]',
            ].join(' ')}
          >
            <div className="flex items-start gap-3">
              <span
                className={[
                  'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  selected
                    ? 'bg-[var(--signup-accent-subtle)] text-[var(--signup-accent)]'
                    : 'bg-[var(--signup-bg-subtle)] text-[var(--signup-text-tertiary)]',
                ].join(' ')}
                aria-hidden
              >
                <Icon size={18} strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <p
                  className={[
                    'text-[15px] font-medium leading-snug',
                    selected
                      ? 'text-[var(--signup-accent)]'
                      : 'text-[var(--signup-text-primary)]',
                  ].join(' ')}
                >
                  {opt.title}
                </p>
                <p className="mt-1 text-[13px] text-[var(--signup-text-secondary)] leading-snug">
                  {opt.description}
                </p>
              </div>
              <RadioDot selected={selected} />
            </div>
          </button>
        )
      })}
    </div>
  )
}

/* ───────────── Small primitives ───────────── */

function RadioCardGroup({ ariaLabelledBy, options, selectedId, onSelect }) {
  return (
    <div
      className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-4"
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
    >
      {options.map((opt) => {
        const selected = selectedId === opt.id
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(opt.id)}
            className={[
              'flex-1 text-left rounded-xl p-5 cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)]',
              'motion-safe:transition-[border-color,background-color,box-shadow] motion-safe:duration-150',
              selected
                ? 'border-2 border-[var(--signup-accent)] bg-[rgba(14,154,160,0.04)]'
                : 'border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-elevated)] hover:border-[var(--signup-border-strong)]',
            ].join(' ')}
          >
            <div className="flex items-start gap-3">
              <RadioDot selected={selected} />
              <div className="min-w-0 flex-1">
                <p
                  className={[
                    'text-[15px] font-medium leading-snug',
                    selected
                      ? 'text-[var(--signup-accent)]'
                      : 'text-[var(--signup-text-primary)]',
                  ].join(' ')}
                >
                  {opt.title}
                </p>
                <p className="mt-1 text-[13px] text-[var(--signup-text-secondary)] leading-[1.5]">
                  {opt.description}
                </p>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 flex items-center gap-2 text-[14px] font-medium text-[var(--signup-text-primary)]"
    >
      <span>{children}</span>
      <span className="text-[12px] font-normal text-[var(--signup-text-tertiary)]">
        (optional)
      </span>
    </label>
  )
}

function FieldHelp({ children }) {
  return (
    <p className="mt-2 text-[12px] text-[var(--signup-text-tertiary)] leading-snug">
      {children}
    </p>
  )
}

function RadioDot({ selected }) {
  return (
    <span
      aria-hidden
      className={[
        'mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2',
        'motion-safe:transition-colors motion-safe:duration-150',
        selected
          ? 'border-[var(--signup-accent)]'
          : 'border-[var(--signup-border-strong)]',
      ].join(' ')}
    >
      {selected ? (
        <span className="block h-[8px] w-[8px] rounded-full bg-[var(--signup-accent)]" />
      ) : null}
    </span>
  )
}
